import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { InfoWindow, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Box, Tooltip } from "@mui/material";

import AdvancedMarkerWithRef from "./AdvancedMarkerWithRef";
import PopUpContent from "./PopUpContent";
import { formatString, findNearestVehicle, getVehicleIgnitionStatus } from "../../../../functions/functions";
import { VehicleDriverChangeType, VehicleGridType } from "../../../../types/types";
import GVehicleLiveTracker from "./GvehicleLiveTracker";

type LatLng = { lat: number; lng: number };

export type GPositionsMarkersHandle = {
    focusOnNearbyFromPoint: (origin: VehicleGridType) => void;
};

const GPositionsMarkers = forwardRef<GPositionsMarkersHandle, {
    gridSelected?: VehicleGridType;
    positions: VehicleGridType[];
    setActions: (item: VehicleGridType) => void;
    setValue: (value: number) => void;
    onLiveModeChange?: (vehicleId: number | null) => void;
    liveVehicleId?: number | null;
    routeToken: string;
    routeCustomerId: number;
    driverChanges?: VehicleDriverChangeType[];
    driverHistoryLoading?: boolean;
    driverHistoryError?: boolean;
    useTransparentBackGround?: boolean;
    activeNearbyOriginId?: string | null;
    onNearbyRouteChange?: (info: {
        origin: VehicleGridType;
        nearest: VehicleGridType;
        distanceText: string;
        durationText: string;
    } | null) => void;
    // Incrementa a cada clique na linha da grade; pede para voltar a seguir o veículo no mapa
    followRequest?: number;
}>(({
    gridSelected,
    positions,
    setActions,
    setValue,
    onLiveModeChange,
    liveVehicleId,
    routeToken,
    routeCustomerId,
    driverChanges,
    driverHistoryLoading,
    driverHistoryError,
    useTransparentBackGround,
    activeNearbyOriginId,
    onNearbyRouteChange,
    followRequest,
}, ref) => {
    const map = useMap();
    const routesLibrary = useMapsLibrary('routes');

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [infoWindowShown, setInfoWindowShown] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState<LatLng | null>(null);

    const [liveMode, setLiveMode] = useState(false);
    const [animateLiveEntry, setAnimateLiveEntry] = useState(true);

    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
    const nearbyRequestVersionRef = useRef(0);
    const liveModeRef = useRef(liveMode);
    liveModeRef.current = liveMode;
    const markersRef = useRef<Record<string, google.maps.marker.AdvancedMarkerElement>>({});
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const lastGridSelectedRef = useRef<string | null>(null);

    /* ---------------- LIVE MODE NOTIFIER ---------------- */
    useEffect(() => {
        if (onLiveModeChange) {
            onLiveModeChange(liveMode && selectedId && Number(selectedId) > 0 ? Number(selectedId) : null);
        }
    }, [liveMode, selectedId, onLiveModeChange]);

    useEffect(() => () => onLiveModeChange?.(null), [onLiveModeChange]);

    useEffect(() => {
        if (liveVehicleId === null) setLiveMode(false);
    }, [liveVehicleId]);

    // Quando o liveMode mudar para true, limpa a rota do veículo próximo
    useEffect(() => {
        if (liveMode && onNearbyRouteChange) {
            onNearbyRouteChange(null);
        }
    }, [liveMode, onNearbyRouteChange]);

    /* ---------------- CLUSTER ---------------- */

    const selectedItem = useMemo(
        () => positions.find((p) => String(p.id) === selectedId),
        [positions, selectedId]
    );

    useEffect(() => {
        if (!map) return;
        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({ map, algorithmOptions: { maxZoom: 16 } });
        }
    }, [map]);

    // Lista de veículos que a câmera já enquadrou. As posições chegam de novo a cada atualização da grade;
    // a câmera só se move quando o conjunto de veículos muda, senão desfaria o arrasto/zoom do usuário.
    const framedVehiclesKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!map || !clustererRef.current) return;

        const vehiclesKey = positions.map((it) => String(it.id)).join(",");
        const vehiclesChanged = framedVehiclesKeyRef.current !== vehiclesKey;

        if (positions.length === 1 && !liveMode) {
            const validPos = positions[0];

            Object.keys(markersRef.current).forEach((id) => {
                if (id !== String(validPos?.id)) {
                    if (markersRef.current[id]) markersRef.current[id].map = null;
                    delete markersRef.current[id];
                }
            });

            if (vehiclesChanged) {
                framedVehiclesKeyRef.current = vehiclesKey;
                map?.panTo({
                    lat: validPos.position.lat as number,
                    lng: validPos.position.lng as number,
                });
                map?.setZoom(20);
            }
            clustererRef.current?.clearMarkers();
            clustererRef.current?.addMarkers(Object.values(markersRef.current));
        } else if (positions.length === 0) {
            Object.keys(markersRef.current).forEach((id) => {
                if (markersRef.current[id]) markersRef.current[id].map = null;
            });
            markersRef.current = {};

            clustererRef.current?.clearMarkers();
            if (vehiclesChanged) {
                framedVehiclesKeyRef.current = vehiclesKey;
                map?.setZoom(5);
            }
        } else if (positions.length > 1 && positions.length !== Object.keys(markersRef.current).length && !liveMode) {
            const validIds = positions.map((it) => String(it.id));

            Object.keys(markersRef.current).forEach((id) => {
                if (!validIds.includes(id)) {
                    if (markersRef.current[id]) markersRef.current[id].map = null;
                    delete markersRef.current[id];
                }
            });

            if (vehiclesChanged) {
                framedVehiclesKeyRef.current = vehiclesKey;
                const firstPos = positions[0];
                map?.panTo({
                    lat: firstPos.position.lat as number,
                    lng: firstPos.position.lng as number,
                });
            }

            clustererRef.current?.clearMarkers();
            clustererRef.current?.addMarkers(Object.values(markersRef.current));

        } else if (selectedItem && liveMode) {
            // Remove TODOS os marcadores do modo estático para não duplicar com o LiveTracker
            Object.keys(markersRef.current).forEach((id) => {
                if (markersRef.current[id]) {
                    markersRef.current[id].map = null;
                }
                delete markersRef.current[id];
            });

            clustererRef.current?.clearMarkers();
        } else {
            clustererRef.current?.clearMarkers();
            clustererRef.current?.addMarkers(Object.values(markersRef.current));
        }
    }, [positions, map, liveMode, selectedItem]);

    /* ---------------- ROUTES RENDERER ---------------- */
    useEffect(() => {
        if (!routesLibrary || !map) return;
        nearbyRequestVersionRef.current++;

        if (!directionsRendererRef.current) {
            directionsRendererRef.current = new routesLibrary.DirectionsRenderer({
                suppressMarkers: true,
                polylineOptions: {
                    strokeColor: "#FF0000",
                    strokeOpacity: 1,
                    strokeWeight: 5,
                },
            });
        }
        directionsRendererRef.current.setMap(liveMode ? null : map);

        return () => {
            directionsRendererRef.current?.setMap(null);
        };
    }, [routesLibrary, map, liveMode]);

    // Limpa a rota quando o card de resumo é fechado no DashMap
    useEffect(() => {
        if ((liveMode || !activeNearbyOriginId) && directionsRendererRef.current) {
            nearbyRequestVersionRef.current++;
            directionsRendererRef.current.setDirections({ routes: [] } as any);
        }
    }, [activeNearbyOriginId, liveMode]);

    /* ---------------- HELPERS ---------------- */

    const handleTrackingChange = useCallback((enabled: boolean) => {
        setAnimateLiveEntry(enabled);
        setLiveMode(enabled);
    }, []);

    const closePopup = useCallback(() => {
        setLiveMode(false);
        setInfoWindowShown(false);
        setSelectedId(null);
        setSelectedPosition(null);
    }, []);

    const renderCustomPin = useCallback((type: string) => {
        return (
            <Box className="custom-pin">
                <img
                    alt="vehicle-icon"
                    src={require(`../../../../assets/files/cars/png/${formatString(type)}.png`)}
                    width={35}
                    height={35}
                />
            </Box>
        );
    }, []);

    /* ---------------- MARKER CLICK ---------------- */

    const onMarkerClick = useCallback(
        (id: string, marker: google.maps.marker.AdvancedMarkerElement) => {
            const position = marker.position;
            if (!position) return;

            map?.panTo({ lat: position.lat as number, lng: position.lng as number });

            if (id === selectedId) {
                setInfoWindowShown((v) => !v);
                return;
            }

            setSelectedId(id);
            setSelectedPosition({ lat: position.lat as number, lng: position.lng as number });
            setInfoWindowShown(true);
        },
        [map, selectedId]
    );

    const focusOnNearby = useCallback((origin: VehicleGridType) => {
        if (!routesLibrary || !map || !directionsRendererRef.current) return;

        const nearest = findNearestVehicle(origin as any, positions as any);
        if (!nearest || !origin.position || !nearest.position) return;

        setLiveMode(false); // evita conflito de zoom/pan

        const directionsService = new routesLibrary.DirectionsService();
        const requestVersion = ++nearbyRequestVersionRef.current;

        directionsService.route(
            {
                origin: { lat: origin.position.lat, lng: origin.position.lng },
                destination: { lat: nearest.position.lat, lng: nearest.position.lng },
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (liveModeRef.current || directionsRendererRef.current?.getMap() !== map || requestVersion !== nearbyRequestVersionRef.current) return;
                if (status === google.maps.DirectionsStatus.OK && result) {
                    directionsRendererRef.current?.setDirections(result);

                    const leg = result.routes[0].legs[0];
                    if (onNearbyRouteChange) {
                        onNearbyRouteChange({
                            origin,
                            nearest,
                            distanceText: leg.distance?.text ?? "",
                            durationText: leg.duration?.text ?? "",
                        });
                    }
                }
            }
        );
    }, [routesLibrary, map, positions, onNearbyRouteChange]);

    useImperativeHandle(ref, () => ({
        focusOnNearbyFromPoint: focusOnNearby,
    }), [focusOnNearby]);

    /* ---------------- GRID SELECT ---------------- */

    const onGridSelect = useCallback(
        (id: string) => {
            if (id === selectedId && infoWindowShown) return;
            if (liveMode) {
                if (id === selectedId) return;
                if (!positions.some(item => String(item.id) === id)) return;
                setAnimateLiveEntry(false);
                setSelectedId(id);
                return;
            }

            const marker = markersRef.current[id];
            if (!marker?.position) return;

            map?.panTo({
                lat: marker.position.lat as number,
                lng: marker.position.lng as number,
            });

            map?.setZoom(20);

            setSelectedId(id);
            setSelectedPosition({
                lat: marker.position.lat as number,
                lng: marker.position.lng as number,
            });
            setInfoWindowShown(true);
        },
        [map, selectedId, infoWindowShown, liveMode, positions]
    );

    /* ---------------- GRID EFFECT ---------------- */

    useEffect(() => {
        if (!gridSelected?.id) return;

        const id = String(gridSelected.id);
        if (lastGridSelectedRef.current === id) return;

        lastGridSelectedRef.current = id;
        onGridSelect(id);
    }, [gridSelected?.id, onGridSelect]);

    /* ---------------- SELECTED ITEM ---------------- */

    useEffect(() => {
        if (
            selectedId &&
            !positions.some((p) => String(p.id) === selectedId)
        ) {
            closePopup();
        }
    }, [positions, selectedId, closePopup]);

    /* ---------------- RENDER ---------------- */

    return (
        <>
            {liveMode && selectedItem ? (
                <GVehicleLiveTracker
                    key={selectedItem.id}
                    vehicle={selectedItem}
                    positions={positions}
                    driverChanges={driverChanges ?? []}
                    driverHistoryLoading={driverHistoryLoading}
                    driverHistoryError={driverHistoryError}
                    animateEntry={animateLiveEntry}
                    setTracking={handleTrackingChange}
                    routeToken={routeToken}
                    routeCustomerId={routeCustomerId}
                    followRequest={followRequest}
                />
            ) : (
                <>
                    {positions?.map((item: any) => {
                        const status = getVehicleIgnitionStatus(item?.attributes, item?.position?.protocol, item?.idle);
                        return (
                            <AdvancedMarkerWithRef
                                key={item.id}
                                className={`marker-${item.id}`}
                                getRef={(ref) => {
                                    markersRef.current[String(item.id)] = ref;
                                }}
                                position={{
                                    lat: item.position.lat,
                                    lng: item.position.lng,
                                }}
                                clickable
                                collisionBehavior="REQUIRED_AND_HIDES_OPTIONAL"
                                onMarkerClick={(marker) => onMarkerClick(String(item.id), marker)}
                            >
                                <div style={{ textAlign: "center" }}>
                                    {renderCustomPin(item.vehicleTypeName)}
                                    <Tooltip title={status.label} arrow>
                                        <div
                                            role="group"
                                            aria-label={`${item.name} - ${item.vehiclePlate}: ${status.label}`}
                                            tabIndex={0}
                                            className={`vehicle-marker-label${useTransparentBackGround ? " vehicle-marker-label-transparent" : ""}`}
                                            style={{ background: useTransparentBackGround ? "transparent" : "#e6e3e3", padding: 6, borderRadius: 4 }}
                                        >
                                            {!useTransparentBackGround && (
                                                <div
                                                    style={{
                                                        background: status.color,
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 4,
                                                        margin: "0 auto",
                                                    }}
                                                />
                                            )}
                                            <div className={`${useTransparentBackGround ? "tooltip-transparent" : ""} vehicle-marker-label-text`} style={{ fontSize: 12, fontWeight: "bold", whiteSpace: "nowrap" }}>
                                                {item.name} - {item.vehiclePlate}
                                            </div>
                                        </div>
                                    </Tooltip>
                                </div>
                            </AdvancedMarkerWithRef>
                        );
                    })}
                </>
            )}

            {infoWindowShown && selectedPosition && selectedItem && !liveMode && (
                <InfoWindow
                    className="Popup"
                    position={selectedPosition}
                    pixelOffset={[0, -65]}
                    shouldFocus={false}
                    onCloseClick={closePopup}
                >
                    <PopUpContent
                        selected={selectedItem as any}
                        setActions={() => setActions(selectedItem)}
                        setValue={setValue}
                        withLivingTracking={true}
                        setTracking={handleTrackingChange}
                        onFocusNearby={() => focusOnNearby(selectedItem)}
                    />
                </InfoWindow>
            )}
        </>
    );
});

GPositionsMarkers.displayName = "GPositionsMarkers";

export default GPositionsMarkers;
